import { z } from 'zod';
/**
 * The two mandatory taxonomies.
 *
 * Every content object in ONOV carries exactly one value from each. This is the
 * single source of truth for both — the API's Mongoose schemas, the mobile
 * composers and the admin console all derive from here.
 *
 * See docs/01-PRD.md §6.4, docs/03-Feature-Specification.md §0.7 and
 * AC-GL-17: creation is blocked if either value is missing. Defining them once
 * is what makes that a compile error rather than a runtime check
 * (docs/06-Technology-Stack.md §4, ADR-10).
 */
export const CATEGORY_KEY_MAX_LENGTH = 40;
export const CATEGORY_KEY_PATTERN = /^[a-z][a-z0-9_]*$/;
/** Runtime category IDs. Existence/capabilities are checked by the API registry. */
export const CategoryKey = z
    .string()
    .min(1)
    .max(CATEGORY_KEY_MAX_LENGTH)
    .regex(CATEGORY_KEY_PATTERN, 'lower_snake_case');
export const Category1 = CategoryKey;
/** Historical IDs remain readable; this list is not a runtime whitelist. */
export const LEGACY_CATEGORY_KEYS = [
    'political',
    'entertainment',
    'hobbies_sports',
    'business',
];
export const ONBOARDING_CATEGORY_KEYS = [
    'political',
    'business',
    'sports',
    'entertainment',
    'family',
    'hobbies',
    'community',
    'shopping',
    'education',
    'volunteering',
    'travel', // Stable legacy ID; displayed as Religion.
    'news',
];
/** Category 2 — how far the content reaches. */
export const Category2 = z.enum(['local', 'state', 'national', 'world']);
/**
 * Mixin applied to every content object. Both fields are required with no
 * default — a default would quietly satisfy AC-GL-17 without the author ever
 * having chosen, which is exactly the failure mode docs/02 §11 warns about.
 */
export const Taxonomy = z.object({
    category1: Category1,
    category2: Category2,
});
/**
 * Seed display labels. The registry is the runtime source (FR-ADM-60) — these
 * are what a fresh database starts with and what a client without the registry
 * falls back to.
 *
 * Labels track the product design; the KEYS never move. Historical content
 * keeps the `entertainment` key, but its display label is now "Entertainment"
 * because Sports has its own runtime category. Renaming persisted keys would
 * require rewriting the taxonomy on every post, event, business and community.
 */
const SEED_CATEGORY_LABELS = {
    political: 'Political',
    entertainment: 'Entertainment',
    hobbies_sports: 'Family/Hobbies',
    business: 'Business',
    community: 'Community',
    family: 'Family',
    sports: 'Sports',
    hobbies: 'Hobbies',
    shopping: 'Shopping',
    education: 'Education',
    volunteering: 'Volunteering',
    travel: 'Religion',
    news: 'News',
};
export const CATEGORY1_LABELS = SEED_CATEGORY_LABELS;
export const categoryLabel = (key) => CATEGORY1_LABELS[key] ?? key;
export const CATEGORY2_LABELS = {
    local: 'Local',
    state: 'State',
    national: 'National',
    world: 'International',
};
/** @deprecated Seed IDs only. Validate runtime category IDs against the registry. */
export const CATEGORY1_VALUES = LEGACY_CATEGORY_KEYS;
export const CATEGORY2_VALUES = Category2.options;
// ─────────────────────────────────────────────────────────────────────────────
// Runtime registry (FR-ADM-60, FR-ADM-62)
// ─────────────────────────────────────────────────────────────────────────────
/** Approved write tokens; readers can fall back for future presentation IDs. */
export const CategoryAccent = z.enum([
    'blue',
    'red',
    'green',
    'amber',
    'orange',
    'purple',
    'pink',
    'navy',
]);
export const CategoryIcon = z.enum([
    'users',
    'user',
    'briefcase',
    'flag',
    'globe',
    'heart',
    'calendar',
    'gift',
    'video',
    'image',
    'star',
    'book',
    'ball',
    'palette',
    'cart',
    'cap',
    'plane',
    'news',
    'music',
    'landmark',
]);
export const CategoryPresentation = z.object({
    onboardingLabel: z.string().min(1).max(80).optional(),
    profileLabel: z.string().min(1).max(80).optional(),
    // Readers accept future tokens and render a fallback. Writes use the approved enums.
    iconKey: z
        .string()
        .min(1)
        .max(40)
        .regex(/^[a-z][a-z0-9_]*$/)
        .optional(),
    accentToken: z
        .string()
        .min(1)
        .max(40)
        .regex(/^[a-z][a-z0-9_]*$/)
        .optional(),
    selectableInOnboarding: z.boolean().optional(),
    contentEnabled: z.boolean().optional(),
    profileEnabled: z.boolean().optional(),
    legacy: z.boolean().optional(),
    /** Server-controlled donation capability; omission means donations are unavailable. */
    donations: z
        .object({
        enabled: z.boolean(),
        recipientKinds: z
            .array(z.enum(['personal', 'nonprofit', 'political_campaign']))
            .min(1)
            .max(3),
        defaultSelected: z.boolean().default(false),
        jurisdictions: z.array(z.string().min(2).max(10)).max(250).optional(),
    })
        .optional(),
});
/** How one profile-section field is rendered and validated. */
/**
 * `image` holds ONE url — the storage module mints it and the app renders a
 * picker rather than a text box. It is a scalar like `text`, not a list like
 * `chips`: a section has one cover photo, not a gallery.
 */
export const ProfileFieldType = z.enum([
    'text',
    'text-list',
    'textarea',
    'select',
    'chips',
    'image',
]);
/**
 * One field on an "Account Settings <Category>" screen.
 *
 * The four screens in the design are the same screen with different field
 * lists, so the list is data rather than four hand-written forms: the app
 * renders whatever the registry describes, and the API compiles the very same
 * definitions into the zod schema it validates saves against. A fifth category
 * therefore needs no app release and no API deploy.
 */
export const ProfileSectionField = z.object({
    key: z
        .string()
        .min(1)
        .max(40)
        .regex(/^[a-zA-Z][a-zA-Z0-9]*$/, 'camelCase'),
    label: z.string().min(1).max(80),
    type: ProfileFieldType,
    required: z.boolean().default(false),
    /** `text`/`textarea`, and each entry of a `text-list`. */
    maxLength: z.number().int().positive().max(5_000).optional(),
    /** `text-list`/`chips`: how many entries the `+` affordance may add. */
    maxItems: z.number().int().positive().max(50).optional(),
    /** `select`/`chips`: the allowed values. */
    options: z.array(z.string().min(1).max(80)).max(200).optional(),
    placeholder: z.string().max(120).optional(),
});
export const ProfileSectionDefinition = z.object({
    /** Screen heading — "Create Political account". */
    title: z.string().min(1).max(120),
    fields: z.array(ProfileSectionField).max(30),
});
/**
 * One Category-1 entry as the apps read it. `key` is append-only: an admin may
 * relabel, reorder or disable, never rename or delete — a vanished key orphans
 * data in every collection that stamped it.
 */
export const CategoryDefinitionView = CategoryPresentation.extend({
    key: CategoryKey,
    label: z.string(),
    order: z.number().int(),
    enabled: z.boolean(),
    /** Absent when the category has no "area of interest" screen. */
    profileSection: ProfileSectionDefinition.optional(),
});
/** One "What I Like" chip from the catalogue backing FR-PROF-05 (FR-ADM-62). */
export const InterestOptionView = z.object({
    id: z.string(),
    category1: CategoryKey,
    label: z.string(),
    icon: z.string().optional(),
    order: z.number().int(),
});
export const InterestQuery = z.object({ category1: CategoryKey.optional() });
/** Historical definitions are public metadata; opted-in clients must support runtime IDs. */
export const CategoryListQuery = z.object({
    includeDisabled: z
        .union([z.boolean(), z.enum(['true', 'false']).transform((value) => value === 'true')])
        .optional(),
});
//# sourceMappingURL=taxonomy.js.map